import { useState, useEffect, useRef } from "react";
import { QuestionDto, AssessmentModuleVersionDto } from "../../models/assessment-module";
import { assessmentService } from "../../services/assessmentService";
import { questionService } from "../../services/questionService";
import { QuestionCard } from "./QuestionCard";
import { QuestionForm } from "./QuestionForm";
import { answerCreateDtoToReturnDto, QuestionCreateDto, questionCreateDtoToUpdateDto, questionDtoToCreateDto, QuestionUpdateDto } from "../../models/assessment-modules-create";
import { StaticFileDto } from "../../models/static-file";
import { BulkQuestionImportResult } from "../../models/bulk-import";

type PreviewFile = File | StaticFileDto;

interface Props {
  moduleId: string;
  moduleVersionId: string;
  moduleVersionIsPublished: boolean;
  onQuestionsChange?: (questions: QuestionDto[]) => void;
  onVersionDataLoaded?: (versionData: AssessmentModuleVersionDto) => void;
}

export const QuestionList = ({ moduleId, moduleVersionId, moduleVersionIsPublished, onQuestionsChange, onVersionDataLoaded }: Props) => {
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [staticFiles, setStaticFiles] = useState<PreviewFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionCreateDto | null>(null);
  const [answerAttachmentNames, setAnswerAttachmentNames] = useState<Record<string, string>>({});
  const [answerAttachmentUrls, setAnswerAttachmentUrls] = useState<Record<string, string>>({});
  const [bulkQuestionFile, setBulkQuestionFile] = useState<File | null>(null);
  const [bulkQuestionSubmitting, setBulkQuestionSubmitting] = useState(false);
  const [bulkQuestionResult, setBulkQuestionResult] = useState<BulkQuestionImportResult | null>(null);
  const bulkQuestionInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadQuestions();
  }, [moduleVersionId]);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await assessmentService.fetchModuleLatestVersion(moduleVersionId);
      if (!response.isFailed) {
        // Pass the complete version data back to parent
        onVersionDataLoaded?.(response.data);
        
        // Make sure we have the latest data with updated file references
        const questions = response.data.questions || [];
        setQuestions(questions);
        onQuestionsChange?.(questions);
      }
    } catch (err) {
      setError('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionAdded = async (newQuestion: QuestionCreateDto) => {
    
    // Calculate the next order using the current array length
    // This ensures sequential ordering: 0, 1, 2, 3, etc.
    const nextOrder = questions.length;
    
    // Update the question with the correct order
    const questionWithCorrectOrder = {
      ...newQuestion,
      order: nextOrder
    };
    
    const response = await questionService.createQuestion(questionWithCorrectOrder);

    if (response.isFailed) {
      setError('Failed to create question. Error: ' + response.errors.join(', '));
      return;
    }

    // Clear static files
    setStaticFiles([]);
    
    // Update the local state with the new question instead of reloading all questions
    setQuestions(prev => {
      const newQuestions = [...prev, response.data];
      onQuestionsChange?.(newQuestions);
      return newQuestions;
    });
    
    setShowAddForm(false);
  };

  const handleQuestionUpdated = async (updatedQuestion: QuestionCreateDto) => {
    const questionToUpdate: QuestionUpdateDto = questionCreateDtoToUpdateDto(updatedQuestion);
    try {
      const response = await questionService.updateQuestion(questionToUpdate);
      if (response.isFailed) {
        setError('Failed to update question. Error: ' + response.errors.join(', '));
        return;
      }

      // Clear the static files state
      setStaticFiles([]);
      
      // Update our local state without refetching everything
      setQuestions(prev => {
        const updatedQuestions = prev.map((q: QuestionDto) =>
          q.id === updatedQuestion.internalId
            ? {
              ...q,
              text: updatedQuestion.text,
              type: updatedQuestion.type,
              answers: response.data.answers || updatedQuestion.answers.map(answerCreateDtoToReturnDto),
              staticFileIds: updatedQuestion.staticFileIds,
              staticFileUrls: response.data.staticFileUrls || [] 
            }
            : q
        );
        onQuestionsChange?.(updatedQuestions);
        return updatedQuestions;
      });

      setEditingQuestion(null);
    } catch (error) {
      setError(`Failed to update question: ${error}`);
    }
  };

  const handleQuestionDeleted = async (questionId: string) => {
    const response = await questionService.deleteQuestion(questionId);

    if (response.isFailed) {
      setError('Failed to delete question. Error: ' + response.errors.join(', '));
      return;
    }

    setQuestions(prev => {
      const filteredQuestions = prev.filter(q => q.id !== questionId);
      onQuestionsChange?.(filteredQuestions);
      return filteredQuestions;
    });
  };

  const handleBulkQuestionImport = async () => {
    if (!bulkQuestionFile) return;

    setBulkQuestionSubmitting(true);
    setError(null);
    setBulkQuestionResult(null);
    try {
      const response = await questionService.importQuestions(moduleVersionId, bulkQuestionFile);
      if (!response.isSuccess || !response.data) {
        setError(response.message || response.errors.join(', ') || 'The question import could not be completed.');
        return;
      }

      setBulkQuestionResult(response.data);
      setBulkQuestionFile(null);
      if (bulkQuestionInputRef.current) bulkQuestionInputRef.current.value = '';
      await loadQuestions();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.errors?.join(', ')
        || requestError?.response?.data?.message
        || requestError?.message
        || 'The question import could not be completed.');
    } finally {
      setBulkQuestionSubmitting(false);
    }
  };

  const handleQuestionEdit = async (question: QuestionDto) => {
    // Preload files for editing - question-level attachments
    const urls = question.staticFileUrls || [];
    
    // Create array of StaticFileDto objects from URLs
    const staticFileDtos = urls.map((url, index) => {
      const fileId = question.staticFileIds?.[index] || `temp-${index}`;
      const fileName = url.split('/').pop() || `File-${index}`;
      const fileType = getFileTypeFromExtension(fileName);
      
      return {
        id: fileId,
        url: url,
        name: fileName,
        type: fileType
      };
    });
    
    // Create mapping of answer attachment file IDs to names and URLs for better display
    const answerAttachmentNames: Record<string, string> = {};
    const answerAttachmentUrls: Record<string, string> = {};
    question.answers.forEach((answer) => {
      if (answer.staticFileIds && answer.staticFileUrls) {
        answer.staticFileIds.forEach((id, index) => {
          if (answer.staticFileUrls && answer.staticFileUrls[index]) {
            const url = Array.from(answer.staticFileUrls)[index];
            const fileName = url.split('/').pop() || `Attachment-${index + 1}`;
            answerAttachmentNames[id] = fileName;
            answerAttachmentUrls[id] = url;
          }
        });
      }
    });
    
    setStaticFiles(staticFileDtos);
    setEditingQuestion(questionDtoToCreateDto(question));
    setAnswerAttachmentNames(answerAttachmentNames);
    setAnswerAttachmentUrls(answerAttachmentUrls);
  };
  
  // Helper to determine file type from extension
  const getFileTypeFromExtension = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return 'image/' + extension;
    } else if (['mp3', 'wav', 'ogg'].includes(extension)) {
      return 'audio/' + extension;
    } else if (['mp4', 'avi', 'mov', 'webm'].includes(extension)) {
      return 'video/' + extension;
    } else if (extension === 'pdf') {
      return 'application/pdf';
    } else {
      return 'application/octet-stream';
    }
  };

  // This helper is no longer needed as we're using StaticFileDto objects directly
  // Keeping it commented in case we need to fetch actual File objects in the future
  /*
  const fetchFilesFromUrls = async (urls: string[]): Promise<File[]> => {
    const files: File[] = [];
    for (const url of urls) {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = await response.data;
      const filename = url.split('/').pop() || 'attachment';
      files.push(new File([blob], filename, { type: blob.type }));
    }
    return files;
  }
  */

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading questions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorText}>{error}</div>
        <button
          onClick={() => setError(null)}
          style={styles.dismissButton}
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Questions ({questions.length})</h2>
      </div>

      {!moduleVersionIsPublished && (
        <div style={styles.bulkImportPanel}>
          <div style={styles.bulkImportCopy}>
            <strong>Import questions in bulk</strong>
            <span style={styles.bulkImportCopyText}>Use TXT for text-only questions, or DOCX when questions contain embedded images. The import validates the whole document before saving.</span>
          </div>
          <div style={styles.bulkImportControls}>
            <input
              ref={bulkQuestionInputRef}
              type="file"
              style={styles.bulkImportFileInput}
              accept=".txt,.docx,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={event => {
                setBulkQuestionFile(event.target.files?.[0] || null);
                setBulkQuestionResult(null);
                setError(null);
              }}
              aria-label="Question import document"
            />
            <a
              href="/templates/academy-questions-template.txt"
              download="academy-questions-template.txt"
              style={styles.bulkImportTemplateButton}
              title="Download a question template"
            >
              Download template
            </a>
            <button
              type="button"
              style={styles.bulkImportButton}
              onClick={() => void handleBulkQuestionImport()}
              disabled={!bulkQuestionFile || bulkQuestionSubmitting}
            >
              {bulkQuestionSubmitting ? 'Importing…' : 'Import questions'}
            </button>
          </div>
          <small style={styles.bulkImportHint}>Required headings: “Question 1: …”, “A. …”, “Correct: A”. Add “Type: MultipleChoice” when more than one answer is correct.</small>
        </div>
      )}

      {bulkQuestionResult && (
        <div style={styles.bulkImportResult}>
          <strong>{bulkQuestionResult.questionsImported} question{bulkQuestionResult.questionsImported === 1 ? '' : 's'} imported from {bulkQuestionResult.format}</strong>
          <span>{bulkQuestionResult.embeddedAssetsImported} embedded asset{bulkQuestionResult.embeddedAssetsImported === 1 ? '' : 's'} attached.</span>
        </div>
      )}

      {questions.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No questions added yet</p>
          {!moduleVersionIsPublished ? (
            <button
              onClick={() => setShowAddForm(true)}
              style={styles.createFirstButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2980b9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#478b12';
              }}
            >
              Create First Question
            </button>
          ) : (
            <div style={styles.publishedEmptyHint}>
              <span style={styles.emptyHintText}>
                📋 This is a published module. Create a new version to add questions.
              </span>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.questionsGrid}>
          {questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              readonly={moduleVersionIsPublished}
              question={question}
              index={index + 1}
              onEdit={() => handleQuestionEdit(question)}
              onDelete={() => handleQuestionDeleted(question.id)}
            />
          ))}
        </div>
      )}

      {/* Add Question Button at Bottom */}
      {!moduleVersionIsPublished && questions.length > 0 && (
        <div style={styles.bottomButtonContainer}>
          <button
            onClick={() => setShowAddForm(true)}
            style={styles.bottomAddButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2980b9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#478b12';
            }}
          >
            + Add Question
          </button>
        </div>
      )}

      {showAddForm && (
        <QuestionForm
          moduleId={moduleId}
          staticFiles={staticFiles}
          setStaticFiles={setStaticFiles}
          moduleVersionId={moduleVersionId}
          onClose={() => {
            setShowAddForm(false);
            setStaticFiles([]); // Clear static files when closing the form
          }}
          onSuccess={handleQuestionAdded}
        />
      )}

      {editingQuestion && (
        <QuestionForm
          moduleId={moduleId}
          staticFiles={staticFiles}
          setStaticFiles={setStaticFiles}
          moduleVersionId={moduleVersionId}
          question={editingQuestion}
          answerAttachmentNames={answerAttachmentNames}
          answerAttachmentUrls={answerAttachmentUrls}
          onClose={() => {
            setEditingQuestion(null);
            setStaticFiles([]); // Clear static files when closing the form
            setAnswerAttachmentNames({}); // Clear answer attachment names
            setAnswerAttachmentUrls({}); // Clear answer attachment URLs
          }}
          onSuccess={handleQuestionUpdated}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '20px',
  },
  dismissButton: {
    marginTop: '10px',
    padding: '8px 16px',
    backgroundColor: '#e74c3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e1e8ed',
  },
  bulkImportPanel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    marginBottom: '24px',
    padding: '16px 18px',
    backgroundColor: '#f5fbed',
    border: '1px dashed #b8d88a',
    borderRadius: '8px',
  },
  bulkImportCopy: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    color: '#285709',
  },
  bulkImportCopyText: {
    color: '#64748b',
    fontSize: '13px',
    lineHeight: 1.45,
  },
  bulkImportControls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  bulkImportFileInput: {
    maxWidth: '360px',
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#374151',
    fontSize: '13px',
  },
  bulkImportButton: {
    padding: '9px 14px',
    border: '1px solid #c7f67f',
    borderRadius: '6px',
    backgroundColor: '#d8ff83',
    color: '#182b0d',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  bulkImportTemplateButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 13px',
    border: '1px solid #b8d88a',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#356d0d',
    fontSize: '13px',
    fontWeight: 700,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  bulkImportHint: {
    color: '#64748b',
    fontSize: '12px',
  },
  bulkImportResult: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '3px',
    marginBottom: '20px',
    padding: '12px 16px',
    border: '1px solid #cde6a6',
    borderRadius: '8px',
    backgroundColor: '#f5fbed',
    color: '#356d0d',
    fontSize: '13px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: 0,
  },
  addButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#478b12',
    color: '#fff',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e1e8ed',
  },
  emptyText: {
    fontSize: '16px',
    color: '#6c757d',
    marginBottom: '20px',
    margin: '0 0 20px 0',
  },
  createFirstButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#478b12',
    color: '#fff',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  questionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px 20px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6c757d',
  },
  errorContainer: {
    padding: '20px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  errorText: {
    fontSize: '14px',
    margin: 0,
  },
  publishedHint: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '6px',
    padding: '8px 12px',
  },
  hintText: {
    fontSize: '13px',
    color: '#856404',
    fontWeight: '500',
    margin: 0,
  },
  publishedEmptyHint: {
    backgroundColor: '#e2e3e5',
    border: '1px solid #d6d8db',
    borderRadius: '6px',
    padding: '12px 16px',
  },
  emptyHintText: {
    fontSize: '14px',
    color: '#6c757d',
    fontWeight: '500',
    margin: 0,
  },
  bottomButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1e8ed',
  },
  bottomAddButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#478b12',
    color: '#fff',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
};
